import { Injectable } from '@angular/core';
import { MessageKey } from '../../../models/message/message-key';
import { AddSsrStatus } from '../../../dtos/response/ssr-response/add-ssr-status';
import { LapInfantActionResult } from '../../../dtos/response/lap-infant-response/lap-infant-action-result';
import { LapInfantActionItem } from '../../../dtos/response/lap-infant-response/lap-infant-action-item';
import { LapInfantProcessorResponse } from '../../../dtos/response/lap-infant-response/lap-infant-processor-response';
import { SSRCode } from '../../../models/ssr/ssr-code';

@Injectable({
  providedIn: 'root',
})
export class SaveLapInfantMessageConverter {
  /**
   * Given an Add / Update Lap Infant response, evaluates the response and converts it into a message bar with content object.
   */
  public convertSaveLapInfantErrorMessage(response: LapInfantProcessorResponse) {
    const mappedMessageKeys: MessageKey[] = [];

    // Convert Failures with invalid response. e.g. System Failure, Timeout, etc.
    this.convertSaveInfantSystemFailure(response, mappedMessageKeys);

    if (response?.failedToAddSsrs && response?.failedToAddSsrs?.length > 0) {
      const failures = response?.failedToAddSsrs;
      failures?.forEach((failure) => {
        const failureMessage = failure.failureMessage;
        this.convertSaveInfantFailure(failureMessage, mappedMessageKeys);
      });
    }

    this.LapInfantProcessorErrorMessageConverter(response, mappedMessageKeys);

    // Partial Failure message, this will be on top of other error messages.
    if (response.status === AddSsrStatus.PARTIAL_SUCCESS) {
      mappedMessageKeys?.push(MessageKey.LAP_INFANT_PARTIAL_FAILURE);
    }

    // If we've identified an error and made it this far without mapping a key, we'll map the unknown error key.
    if (response.status !== AddSsrStatus.SUCCESS && mappedMessageKeys?.length === 0) {
      mappedMessageKeys?.push(MessageKey.LAP_INFANT_UNKNOWN_WITH_LINK);
    }

    return mappedMessageKeys;
  }

  LapInfantProcessorErrorMessageConverter(response: LapInfantProcessorResponse, mappedMessageKeys: MessageKey[]) {
    if (response?.lapInfantRemoveFailures && response?.lapInfantRemoveFailures?.length > 0) {
      // Case where both INFT and DOCS SSR failures are present, we show a single message for both.
      if (this.isBothInftSsrAndDocsFailurePresent(response)) {
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_REMOVE_SSR_FAILURE);
        return;
      }
      const updateFailures = response?.lapInfantRemoveFailures;
      updateFailures?.forEach((failure) => {
        this.convertInfantProcessFailure(failure, mappedMessageKeys, response.action);
      });
    }
  }

  private isBothInftSsrAndDocsFailurePresent(response: LapInfantProcessorResponse): boolean {
    return (
      response?.lapInfantRemoveFailures.some((failure) => failure?.type === SSRCode.INFT) &&
      response?.lapInfantRemoveFailures.some((failure) => failure?.type === SSRCode.DOCS)
    );
  }

  private convertSaveInfantSystemFailure(response: LapInfantProcessorResponse, mappedMessageKeys: MessageKey[]) {
    switch (true) {
      case response?.errorMessage?.toLowerCase().includes('timeout'):
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_TIMEOUT);
        break;
      case response?.errorMessage?.toLowerCase().includes('invalid lap infant age'):
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_INVALID_AGE);
        break;
      case response?.errorMessage?.toLowerCase().includes('password decryption error'):
        mappedMessageKeys?.push(MessageKey.PASSWORD_DECRYPTION_ERROR);
        break;
      default:
        break;
    }
  }

  private convertSaveInfantFailure(failureMessage: string | undefined, mappedMessageKeys: MessageKey[]) {
    switch (true) {
      case failureMessage?.toLowerCase().includes('adult is already associated with the lap infant'):
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_ASSOCIATED);
        break;
      case failureMessage?.toLowerCase().includes('unknown error adding lap infant'):
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_UNKNOWN);
        break;
      case failureMessage?.toLowerCase().includes('lap infant cannot be seated in exit row'):
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_RESTRICTED_SEAT);
        break;
      default:
        break;
    }
  }

  private convertInfantProcessFailure(
    lapInfantActionResult: LapInfantActionResult | undefined,
    mappedMessageKeys: MessageKey[],
    lapInfantActionType?: LapInfantActionItem
  ) {
    switch (lapInfantActionResult?.type) {
      case SSRCode.INFT:
      case SSRCode.DOCS:
        mappedMessageKeys?.push(MessageKey.LAP_INFANT_REMOVE_SSR_FAILURE);
        break;
      case 'Passenger':
        mappedMessageKeys?.push(
          lapInfantActionType === LapInfantActionItem.Update
            ? MessageKey.LAP_INFANT_UNKNOWN
            : MessageKey.LAP_INFANT_REMOVE_UNKNOWN_WITH_LINK
        );
        break;
      default:
        break;
    }
  }
}
